import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  getAutomationsQueryKey,
  getScenarioQueryKey,
  listAutomations,
  setAutomationActive,
  type AutomationItem,
  type ScenarioDetail,
} from '../api/automations.client.js';

export function useAutomations() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  return useQuery({
    queryKey: getAutomationsQueryKey(workspaceId),
    queryFn: () => listAutomations(),
    enabled: scope !== null,
  });
}

export function useToggleAutomation() {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  return useMutation<
    AutomationItem,
    Error,
    { id: string; isActive: boolean },
    { previousItem: AutomationItem | undefined }
  >({
    mutationFn: ({ id, isActive }) => setAutomationActive(id, isActive),
    onMutate: async ({ id, isActive }) => {
      await queryClient.cancelQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
      const previous = queryClient.getQueryData<AutomationItem[]>(
        getAutomationsQueryKey(workspaceId),
      );
      const previousItem = previous?.find((item) => item.id === id);
      if (previous) {
        queryClient.setQueryData<AutomationItem[]>(
          getAutomationsQueryKey(workspaceId),
          previous.map((item) => (item.id === id ? { ...item, isActive } : item)),
        );
      }
      return { previousItem };
    },
    onError: (_error, vars, context) => {
      if (context?.previousItem) {
        const current = queryClient.getQueryData<AutomationItem[]>(
          getAutomationsQueryKey(workspaceId),
        );
        if (current) {
          queryClient.setQueryData<AutomationItem[]>(
            getAutomationsQueryKey(workspaceId),
            current.map((item) => (item.id === vars.id ? { ...context.previousItem! } : item)),
          );
        }
      }
    },
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<ScenarioDetail>(
        getScenarioQueryKey(workspaceId, variables.id),
        (detail) => (detail === undefined ? detail : { ...detail, ...updated }),
      );
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: getAutomationsQueryKey(workspaceId) });
      void queryClient.invalidateQueries({
        queryKey: getScenarioQueryKey(workspaceId, variables.id),
      });
    },
  });
}
